import React from 'react';
import { ChevronDown } from 'lucide-react'; // For a custom dropdown arrow icon

/**
 * Reusable Select Box Component.
 * Provides a standard dropdown select input with Tailwind CSS styling.
 * Aims for a consistent look with other form elements, with custom arrow.
 *
 * @param {object} props - Component props.
 * @param {string} props.id - Unique ID for the select and its label.
 * @param {string} props.name - Name attribute for the select.
 * @param {string} props.label - Text label for the select box.
 * @param {Array<object>} props.options - Array of options: [{ value: any, label: string }].
 * @param {any} props.value - Controlled component: current selected value.
 * @param {function} props.onChange - Change event handler.
 * @param {boolean} [props.required=false] - If true, select is required.
 * @param {boolean} [props.disabled=false] - If true, select is disabled.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the select element.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label text.
 * @param {string} [props.containerClassName=''] - Additional custom Tailwind classes for the wrapper div.
 */
const SelectBox = ({
    id,
    name,
    label,
    options = [],
    value,
    onChange,
    required = false,
    disabled = false,
    className = '',
    labelClassName = '',
    containerClassName = '',
    ...props
}) => {
    const selectBaseStyles = `
        appearance-none block w-full px-4 py-2 border border-gray-medium rounded-lg
        bg-white text-text-primary text-base cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue
        transition-all duration-200 ease-in-out
        disabled:bg-gray-light disabled:cursor-not-allowed
    `;

    return (
        <div className={`mb-4 ${containerClassName}`}>
            <label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                {label}
                {required && <span className="text-gmail-red ml-1">*</span>}
            </label>
            <div className="relative">
                <select
                    id={id || name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    required={required}
                    className={`${selectBaseStyles} ${className}`}
                    {...props}
                >
                    <option value="">-- Select an option --</option>
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                {/* Custom arrow icon */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-dark">
                    <ChevronDown size={20} />
                </div>
            </div>
        </div>
    );
};

export default SelectBox;
